/**
 * Payment Journey — second tier
 *
 * Mirrors the fork the submit handler writes as a cookie (see
 * inc/helpers/class-payment-journey-helper.php), and is read on the transaction
 * result page only when that cookie did not arrive.
 *
 * Why a mirror at all: whether a cookie is sent after a chain of redirects is
 * decided by the browser, on rules that differ between browsers and change over
 * time. Browser storage is never sent with a request, so none of that applies —
 * the cashier and the result page share an origin, and what one writes the other
 * reads however many foreign domains the player passed through on the way.
 *
 * It is the weaker tier: anything here is writable by any script on the page, so
 * what it carries has to be safe to accept from a stranger. That is why it carries
 * a word from a list of two and never an address; the address the player goes back
 * to is derived from the word, on the server.
 *
 * @package betbeast
 */

const KEY    = 'rc_payment_journey';
const FLOWS  = [ 'deposit', 'withdrawal' ];
const TTL_MS = 1800 * 1000;

/**
 * Store the fork. Called when the player is about to leave for the provider.
 *
 * Browser storage has no expiry of its own, so the deadline is written into the
 * value — otherwise an abandoned payment would still be colouring the result page
 * weeks later.
 *
 * @param {string} flow 'deposit' or 'withdrawal'.
 * @param {string} pid  Provider payment id, for staleness checks only.
 * @returns {boolean} False when the flow is not one we store, or storage refused.
 */
export function writeJourney( flow, pid = '' ) {
    if ( ! FLOWS.includes( flow ) ) return false;

    try {
        window.localStorage.setItem( KEY, JSON.stringify( {
            flow,
            pid: String( pid || '' ),
            exp: Date.now() + TTL_MS,
        } ) );
        return true;
    } catch ( e ) {
        // Private browsing modes have historically thrown on write. Falling over
        // because a backup channel is unavailable would be worse than not having it.
        return false;
    }
}

/**
 * The fork, or null when there is none to read.
 *
 * Returns null just as readily for a malformed, expired or unknown value: the page
 * then says nothing specific about the transaction, which is the honest answer.
 *
 * @returns {{flow: string, pid: string}|null}
 */
export function readJourney() {
    let raw;

    try {
        raw = window.localStorage.getItem( KEY );
    } catch ( e ) {
        return null;
    }

    if ( ! raw ) return null;

    let parsed;
    try {
        parsed = JSON.parse( raw );
    } catch ( e ) {
        return null;
    }

    if ( ! parsed || typeof parsed !== 'object' ) return null;
    if ( ! FLOWS.includes( parsed.flow ) ) return null;
    if ( ! parsed.exp || Date.now() > parsed.exp ) return null;

    return { flow: parsed.flow, pid: String( parsed.pid || '' ) };
}

/**
 * Drop the fork once it has been used, or once it is known to be stale.
 */
export function clearJourney() {
    try {
        window.localStorage.removeItem( KEY );
    } catch ( e ) {
        // Nothing to do: an unreadable store is also an unwritable one.
    }
}
